import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Train, Landmark, FileText, Award, Shield, BookOpen, Building2, Factory, ArrowRight, Clock, IndianRupee } from "lucide-react";
import { api, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import JobRow from "@/components/JobRow";
import NewsletterCard from "@/components/NewsletterCard";
import EligibilityCalculator from "@/components/widgets/EligibilityCalculator";
import DailyQuiz from "@/components/widgets/DailyQuiz";
import ExamTimeline from "@/components/widgets/ExamTimeline";
import Flashcards from "@/components/widgets/Flashcards";
import NotificationAlarm from "@/components/widgets/NotificationAlarm";
import MatchProfileWizard from "@/components/MatchProfileWizard";
import PushPermissionCard, { PushSubscribedBadge } from "@/components/PushPermissionCard";
import AlertCountPill from "@/components/AlertCountPill";
import TopEmployers from "@/components/TopEmployers";

// Lightweight CTA card pointing to the dedicated salary calculator page.
const SalaryCalculatorCta = () => (
  <section
    className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-5"
    data-testid="salary-cta"
    aria-labelledby="salary-cta-h2"
  >
    <div className="flex items-center gap-2 mb-1">
      <span className="inline-grid place-items-center w-8 h-8 rounded-md bg-green-600 text-white">
        <IndianRupee className="w-4 h-4" aria-hidden="true" />
      </span>
      <h2 id="salary-cta-h2" className="text-lg font-extrabold text-slate-900" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
        Salary-in-hand Calculator
      </h2>
    </div>
    <p className="text-xs text-slate-700 mb-3 leading-relaxed">
      Real take-home for SSC, RRB, IBPS, SBI, RBI, UPSC, KVS posts — 7th CPC + 53% DA + city HRA.
    </p>
    <Link
      to="/tools/salary-calculator"
      data-testid="salary-cta-link"
      className="inline-flex items-center min-h-11 px-4 py-2 rounded-md bg-green-600 hover:bg-green-700 text-white text-sm font-semibold w-full justify-center"
    >
      Calculate your salary →
    </Link>
  </section>
);

const DEPT_ICONS = { Train, Landmark, FileText, Award, Shield, BookOpen, Building2, Factory };

export default function Home() {
  const [jobs, setJobs] = useState([]);
  const [admits, setAdmits] = useState([]);
  const [results, setResults] = useState([]);
  const [cats, setCats] = useState({ departments: [] });
  const [stats, setStats] = useState({ jobs: 0, admit_cards: 0, results: 0, subscribers: 0 });
  const [articles, setArticles] = useState([]);
  const [articleCats, setArticleCats] = useState({});
  const [privateJobs, setPrivateJobs] = useState([]);
  const [privateCompanies, setPrivateCompanies] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/jobs?limit=8&sort=latest").then(r => setJobs(r.data.items));
    api.get("/admit-cards?limit=5").then(r => setAdmits(r.data.items));
    api.get("/results?limit=5").then(r => setResults(r.data.items));
    api.get("/categories").then(r => setCats(r.data));
    api.get("/stats").then(r => setStats(r.data));
    api.get("/articles?limit=6").then(r => { setArticles(r.data.items); setArticleCats(r.data.categories || {}); });
    api.get("/private-jobs?limit=6").then(r => setPrivateJobs(r.data.items || [])).catch(() => {});
    api.get("/private-companies").then(r => setPrivateCompanies(r.data.items || [])).catch(() => {});
  }, []);

  const onSearch = (e) => { e.preventDefault(); if (q) window.location.href = `/all-india-jobs?search=${encodeURIComponent(q)}`; };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="bg-grid-pattern bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20 text-center">
          <div className="flex flex-wrap items-center justify-center gap-2 mb-5">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-50 border border-orange-200 text-orange-700 text-xs font-bold uppercase tracking-wider">
              <span className="w-2 h-2 bg-orange-600 rounded-full animate-pulse"/>
              Refreshed Daily · {stats.jobs}+ Live Vacancies
            </div>
            <AlertCountPill />
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold text-slate-900 tracking-tight" style={{fontFamily:"'Cabinet Grotesk',sans-serif"}}>
            Latest <span className="text-orange-600">Government &amp; Private</span><br/>
            Jobs in India — All in One Place.
          </h1>
          <p className="mt-5 text-base md:text-lg text-slate-700 max-w-2xl mx-auto leading-relaxed">
            hireds.in: latest Sarkari Naukri notifications plus fresh openings from Reliance, TCS, HDFC, Tata, Airtel, Zerodha, Dream11 &amp; more. One trusted portal. Updated every day. Free to use.
          </p>
          <form onSubmit={onSearch} className="mt-8 max-w-2xl mx-auto flex flex-col sm:flex-row gap-2" data-testid="hero-search-form" role="search">
            <div className="relative flex-1">
              <label htmlFor="hero-search" className="sr-only">Search jobs by sector or organization</label>
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" aria-hidden="true"/>
              <Input id="hero-search" data-testid="hero-search-input" value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Search SSC, RRB, Banking, UPSC..." aria-label="Search jobs by sector or organization" className="pl-10 h-12 text-base bg-white border-slate-300"/>
            </div>
            <Button data-testid="hero-search-btn" type="submit" className="h-12 px-7 bg-orange-600 hover:bg-orange-700 text-white font-semibold">Search Jobs</Button>
          </form>

          <MatchProfileWizard />
          <PushPermissionCard />
          <PushSubscribedBadge />

          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 max-w-3xl mx-auto">
            {[
              {label:"Live Jobs", val: stats.jobs},
              {label:"Admit Cards", val: stats.admit_cards},
              {label:"Results", val: stats.results},
              {label:"Subscribers", val: stats.subscribers},
            ].map((s)=>(
              <div key={s.label} className="bg-white border border-slate-200 rounded-lg p-3 md:p-4">
                <div className="text-2xl md:text-3xl font-extrabold text-slate-900">{s.val}+</div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-12">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-wider font-bold text-orange-600">Browse by sector</div>
            <h2 className="text-2xl md:text-3xl font-extrabold mt-1" style={{fontFamily:"'Cabinet Grotesk',sans-serif"}}>Job Categories</h2>
          </div>
          <Link to="/all-india-jobs" data-testid="view-all-categories" className="text-sm font-semibold text-slate-700 hover:text-orange-600 hidden md:inline-flex items-center gap-1">View all <ArrowRight className="w-4 h-4"/></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4">
          {cats.departments.map((d)=>{
            const Ic = DEPT_ICONS[d.icon] || Briefcase;
            return (
              <Link key={d.slug} to={`/all-india-jobs?department=${d.slug}`} data-testid={`cat-${d.slug}`} className="bg-white border border-slate-200 rounded-lg p-5 hover:border-orange-400 hover:shadow-md transition-all group">
                <div className="w-11 h-11 rounded-md bg-orange-50 grid place-items-center group-hover:bg-orange-600 transition-colors">
                  <Ic className="w-5 h-5 text-orange-600 group-hover:text-white"/>
                </div>
                <div className="mt-3 font-semibold text-slate-900">{d.name}</div>
                <div className="text-xs text-slate-500 mt-1">View vacancies →</div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* LATEST JOBS + ADMIT/RESULT BENTO + SIDEBAR WIDGETS */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 grid gap-8 lg:grid-cols-[1fr_340px]">
        {/* MAIN COLUMN */}
        <div className="space-y-8 min-w-0">
          <section aria-labelledby="home-govt-h2" data-testid="home-govt-section">
            <div className="flex items-end justify-between mb-6">
              <div>
                <div className="text-xs uppercase tracking-wider font-bold text-orange-600">Central · State · PSU · Defense</div>
                <h2 id="home-govt-h2" className="text-2xl md:text-3xl font-extrabold mt-1" style={{fontFamily:"'Cabinet Grotesk',sans-serif"}}>Government Jobs — Latest Sarkari Naukri</h2>
              </div>
              <Link to="/govt-jobs" data-testid="view-all-govt-jobs" className="text-sm font-semibold text-slate-700 hover:text-orange-600 inline-flex items-center gap-1">All Govt Jobs <ArrowRight className="w-4 h-4"/></Link>
            </div>
            <div className="space-y-3">
              {jobs.map((j, i) => <JobRow key={j.id} job={j} idx={i}/>)}
            </div>
          </section>

          {/* ADMIT + RESULT BENTO */}
          <section className="grid md:grid-cols-2 gap-6">
            <Card className="p-5 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-extrabold text-xl" style={{fontFamily:"'Cabinet Grotesk',sans-serif"}}>Admit Cards</h3>
                <Link to="/admit-cards" className="text-xs font-bold uppercase tracking-wider text-orange-600 hover:underline" data-testid="all-admits-link">View all</Link>
              </div>
              <ul className="divide-y divide-slate-100">
                {admits.map((a)=>(
                  <li key={a.id} className="py-3">
                    <Link to={`/admit-cards/${a.slug}`} className="block hover:text-orange-600">
                      <div className="font-medium text-sm text-slate-900 leading-snug">{a.title}</div>
                      <div className="text-xs text-slate-500 mt-0.5">Exam: {fmtDate(a.exam_date)} · {a.organization}</div>
                    </Link>
                  </li>
                ))}
              </ul>
            </Card>
            <Card className="p-5 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-extrabold text-xl" style={{fontFamily:"'Cabinet Grotesk',sans-serif"}}>Recent Results</h3>
                <Link to="/results" className="text-xs font-bold uppercase tracking-wider text-orange-600 hover:underline" data-testid="all-results-link">View all</Link>
              </div>
              <ul className="divide-y divide-slate-100">
                {results.map((r)=>(
                  <li key={r.id} className="py-3">
                    <Link to={`/results/${r.slug}`} className="block hover:text-orange-600">
                      <div className="font-medium text-sm text-slate-900 leading-snug">{r.title}</div>
                      <div className="text-xs text-green-700 mt-0.5 font-semibold">DECLARED · {fmtDate(r.result_date)}</div>
                    </Link>
                  </li>
                ))}
              </ul>
            </Card>
          </section>
        </div>

        {/* SIDEBAR — sits below main column on mobile, fixed sidebar on desktop */}
        <aside className="space-y-6 lg:sticky lg:top-20 lg:self-start min-w-0" aria-label="Tools and engagement widgets">
          <EligibilityCalculator />
          <DailyQuiz />
          <SalaryCalculatorCta />
          <Flashcards />
          <ExamTimeline />
        </aside>
      </div>

      {/* PRIVATE SECTOR JOBS — stacked below Govt (best LCP) */}
      <section
        className="max-w-7xl mx-auto px-4 md:px-6 py-8 border-t border-slate-200"
        aria-labelledby="home-private-h2"
        data-testid="home-private-section"
      >
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-wider font-bold text-orange-600">Corporate India · IT · Banking · Manufacturing · Tech Startups</div>
            <h2 id="home-private-h2" className="text-2xl md:text-3xl font-extrabold mt-1" style={{fontFamily:"'Cabinet Grotesk',sans-serif"}}>Private Sector Jobs — Top Employers Hiring Now</h2>
          </div>
          <Link to="/private-jobs" data-testid="view-all-private-jobs" className="text-sm font-semibold text-slate-700 hover:text-orange-600 inline-flex items-center gap-1">All Private Jobs <ArrowRight className="w-4 h-4"/></Link>
        </div>

        {/* Top Employers carousel (CLS-safe text badges) */}
        <TopEmployers companies={privateCompanies} limit={12} />

        {/* Fresh private-sector openings */}
        <div className="mt-8 grid md:grid-cols-2 gap-4" data-testid="home-private-jobs-grid">
          {privateJobs.length === 0 ? (
            <div className="text-sm text-slate-500 col-span-2">Loading private openings…</div>
          ) : (
            privateJobs.map((j) => (
              <Link
                key={j.id}
                to={`/private-jobs/${j.company_slug}/${j.slug}`}
                data-testid={`home-private-row-${j.slug}`}
                className="bg-white border border-slate-200 rounded-lg p-4 hover:border-orange-300 hover:shadow-sm transition-all"
              >
                <div className="flex items-start gap-3">
                  <div
                    className="w-11 h-11 rounded-md grid place-items-center text-xs font-extrabold shrink-0"
                    style={{ background: (j.company && j.company.brand_bg) || "#0f172a", color: (j.company && j.company.brand_color) || "#fff" }}
                    aria-hidden="true"
                  >
                    {((j.company && (j.company.short_name || j.company.name)) || "?").slice(0, 3).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-extrabold text-slate-900 text-sm leading-snug truncate" style={{fontFamily:"'Cabinet Grotesk',sans-serif"}}>{j.title}</h3>
                    <div className="mt-1 text-xs text-slate-600">
                      <span className="font-semibold">{(j.company && (j.company.short_name || j.company.name)) || j.company_slug}</span>
                      {j.location && <> · {j.location}</>}
                    </div>
                    <div className="mt-1 text-xs text-slate-500">
                      {j.experience && <span>{j.experience}</span>}
                      {j.salary_min ? <span> · ₹{j.salary_min.toLocaleString("en-IN")}–{(j.salary_max || j.salary_min).toLocaleString("en-IN")}/mo</span> : null}
                    </div>
                  </div>
                </div>
              </Link>
            ))
          )}
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-12">
        <NewsletterCard/>
      </section>

      {/* SEO content section — about the portal */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-12 grid md:grid-cols-3 gap-8">
        <article className="md:col-span-2">
          <div className="text-xs uppercase tracking-wider font-bold text-orange-600">Why hireds.in</div>
          <h2 className="text-2xl md:text-3xl font-extrabold mt-1 mb-4" style={{fontFamily:"'Cabinet Grotesk',sans-serif"}}>India's trusted portal for serious aspirants</h2>
          <div className="prose prose-slate max-w-none text-slate-700 leading-relaxed space-y-4 text-base">
            <p>Looking for the latest <strong>Sarkari Naukri</strong>? <strong>hireds.in</strong> shows every fresh recruitment notice in India. We cover Railway, SSC, UPSC, IBPS, SBI, Indian Army, Navy, Air Force and every State PSC. Open the site, search, and apply. That is it.</p>
            <p>Each posting page has all you need: who can apply, age limit, fees, salary, last date, exam plan and a direct link to the official PDF. No clutter. No spam. Just clear info to help you decide fast.</p>
            <h3 className="font-bold text-slate-900 text-lg mt-6 mb-2">Sectors we cover</h3>
            <p>Central postings (UPSC, SSC, RRB), Banks (SBI, IBPS, RBI), Defense (Army, Navy, Air Force, Agniveer), Teaching (KVS, NVS, CTET), all State PSCs, and big PSUs (ONGC, GAIL, IOCL, BHEL, NTPC). 10th pass, 12th pass, ITI, diploma or graduate — there is something for you.</p>
            <h3 className="font-bold text-slate-900 text-lg mt-6 mb-2">Refreshed daily</h3>
            <p>Our system pulls fresh notices each morning at 06:00 IST from seven trusted sources. New entries also go to Bing, Yandex and Google in minutes. So you never miss a deadline.</p>
            <h3 className="font-bold text-slate-900 text-lg mt-6 mb-2">Free email alerts</h3>
            <p>Sign up above to get a clean daily or weekly email. No spam. No fake postings. Just the day's verified notices and result alerts. Stop anytime.</p>
            <h3 className="font-bold text-slate-900 text-lg mt-6 mb-2">Why aspirants pick hireds.in</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>One stop for vacancies, admit cards and results.</li>
              <li>Mobile-first. Loads fast on a 2G phone.</li>
              <li>Filter by state, exam, qualification or department.</li>
              <li>Each notice is checked against the official source.</li>
              <li>Free. No login needed. No paywalls.</li>
            </ul>
          </div>

          {/* Trust signals */}
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3" aria-label="Trust signals">
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-md">
              <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5 text-green-700 shrink-0" aria-hidden="true"><path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              <span className="text-xs font-semibold text-green-900">SSL Secured</span>
            </div>
            <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5 text-blue-700 shrink-0" aria-hidden="true"><path d="M12 2L4 5v6c0 5 3.5 9.5 8 11 4.5-1.5 8-6 8-11V5l-8-3z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/></svg>
              <span className="text-xs font-semibold text-blue-900">Verified Sources</span>
            </div>
            <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
              <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5 text-amber-700 shrink-0" aria-hidden="true"><path d="M12 6v6l4 2 M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
              <span className="text-xs font-semibold text-amber-900">Daily at 06:00 IST</span>
            </div>
            <div className="flex items-center gap-2 p-3 bg-purple-50 border border-purple-200 rounded-md">
              <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5 text-purple-700 shrink-0" aria-hidden="true"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2 M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              <span className="text-xs font-semibold text-purple-900">Trusted by Aspirants</span>
            </div>
          </div>

          {/* BLOG SECTION — latest articles, above the author bio */}
          <section className="mt-10" aria-labelledby="home-blog-heading" data-testid="home-blog-section">
            <div className="flex items-end justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-wider font-bold text-orange-600">From our editorial</div>
                <h2 id="home-blog-heading" className="text-2xl md:text-3xl font-extrabold mt-1" style={{fontFamily:"'Cabinet Grotesk',sans-serif"}}>Blog</h2>
              </div>
              <Link to="/blog" data-testid="home-blog-all" className="text-sm font-semibold text-slate-700 hover:text-orange-600 inline-flex items-center gap-1">All articles <ArrowRight className="w-4 h-4"/></Link>
            </div>
            {articles.length === 0 ? (
              <div className="text-sm text-slate-500">Loading articles…</div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {articles.map((a, i) => (
                  <Link key={a.id} to={`/blog/${a.slug}`} data-testid={`home-blog-card-${i}`} className="group bg-white border border-slate-200 rounded-lg p-4 hover:border-orange-300 hover:shadow-md transition-all">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 font-bold uppercase tracking-wider">
                        <BookOpen className="w-3 h-3"/>{articleCats[a.category] || a.category}
                      </span>
                      <span className="text-slate-500 inline-flex items-center gap-1"><Clock className="w-3 h-3"/>{a.reading_time} min</span>
                    </div>
                    <h3 className="font-extrabold text-base text-slate-900 mt-2 leading-snug group-hover:text-orange-600 transition-colors" style={{fontFamily:"'Cabinet Grotesk',sans-serif"}}>{a.title}</h3>
                    <p className="text-sm text-slate-600 mt-2 leading-relaxed line-clamp-2">{a.excerpt}</p>
                  </Link>
                ))}
              </div>
            )}
          </section>

          {/* Author bio block */}
          <div className="mt-8 p-5 bg-white border-2 border-slate-200 rounded-lg flex flex-col sm:flex-row gap-4 items-start" data-testid="author-bio">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-orange-700 grid place-items-center text-white text-2xl font-extrabold shrink-0" aria-hidden="true">D</div>
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-orange-600">Reviewed &amp; verified by</div>
              <h3 className="font-extrabold text-lg text-slate-900 mt-0.5">Dileshwar Vitthale · Chief Editor</h3>
              <p className="text-sm text-slate-700 leading-relaxed mt-1">8+ years tracking Indian government recruitment. Verifies every notice on hireds.in against the official source before publishing. Contact: <a href="mailto:editor@hireds.in" className="font-semibold text-slate-900 hover:text-orange-600">editor@hireds.in</a>.</p>
              <Link to="/about" className="text-xs font-semibold text-orange-600 hover:underline mt-2 inline-block">Read full editorial profile →</Link>
            </div>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-3 text-xs text-slate-500">
            <span><span className="font-semibold">Last updated:</span> <time dateTime={new Date().toISOString().slice(0,10)}>{new Date().toLocaleDateString("en-IN", {day:"2-digit", month:"long", year:"numeric"})}</time></span>
            <span>·</span>
            <span>By <Link to="/about" className="font-semibold hover:text-orange-600">Dileshwar Vitthale</Link></span>
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-2 pt-4 border-t border-slate-200">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Share hireds.in</span>
            <a data-testid="home-share-whatsapp" href="https://api.whatsapp.com/send?text=Latest%20Indian%20govt%20jobs%20daily%20%E2%80%94%20https%3A%2F%2Fhireds.in" target="_blank" rel="noopener noreferrer" aria-label="Share hireds.in on WhatsApp" className="inline-flex items-center justify-center min-h-11 px-4 py-2 rounded-md text-sm font-semibold text-white bg-green-600 hover:bg-green-700">Share on WhatsApp</a>
            <a data-testid="home-share-twitter" href="https://twitter.com/intent/tweet?text=Latest%20Indian%20Sarkari%20Naukri%20daily%20%E2%80%94%20%40Hiredsdotin&url=https%3A%2F%2Fhireds.in" target="_blank" rel="noopener noreferrer" aria-label="Share hireds.in on Twitter" className="inline-flex items-center justify-center min-h-11 px-4 py-2 rounded-md text-sm font-semibold text-white bg-slate-900 hover:bg-slate-800">Share on Twitter</a>
            <a data-testid="home-share-facebook" href="https://www.facebook.com/sharer/sharer.php?u=https%3A%2F%2Fhireds.in" target="_blank" rel="noopener noreferrer" aria-label="Share hireds.in on Facebook" className="inline-flex items-center justify-center min-h-11 px-4 py-2 rounded-md text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700">Share on Facebook</a>
          </div>
        </article>
        <aside className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-lg p-5">
            <div className="text-xs font-bold uppercase tracking-wider text-orange-600 mb-2">Trust &amp; Editorial</div>
            <ul className="space-y-1 text-sm text-slate-700">
              <li><Link to="/editorial-policy" className="block py-2 hover:text-orange-600">Editorial Policy</Link></li>
              <li><Link to="/privacy-policy" className="block py-2 hover:text-orange-600">Privacy Policy</Link></li>
              <li><Link to="/disclaimers" className="block py-2 hover:text-orange-600">Terms of Service &amp; Disclaimers</Link></li>
              <li><Link to="/about" className="block py-2 hover:text-orange-600">About hireds.in</Link></li>
              <li><Link to="/contact" className="block py-2 hover:text-orange-600">Contact our editors</Link></li>
            </ul>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-5">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">Verified sources</div>
            <p className="text-xs text-slate-600 leading-relaxed">All notifications are cross-checked against UPSC, SSC, RRB, IBPS and the official ministry websites before publication. Found an issue? Email <a href="mailto:editor@hireds.in" className="font-semibold text-slate-900 hover:text-orange-600">editor@hireds.in</a> &mdash; corrections applied within 24 hours.</p>
          </div>
        </aside>
      </section>

      {/* FAQ SECTION — matches FAQPage schema in index.html */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-12 border-t border-slate-200" aria-labelledby="faq-heading">
        <div className="text-xs uppercase tracking-wider font-bold text-orange-600">Common questions</div>
        <h2 id="faq-heading" className="text-2xl md:text-3xl font-extrabold mt-1 mb-6" style={{fontFamily:"'Cabinet Grotesk',sans-serif"}}>Frequently Asked Questions</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {[
            { q: "Is hireds.in free to use?", a: "Yes. hireds.in is fully free for every aspirant. No login. No paywall. No hidden fee. Just open the site and read the latest Indian govt vacancies." },
            { q: "How often is the portal refreshed?", a: "Daily at 06:00 IST. Our automation pulls fresh notices from UPSC, SSC, RRB, IBPS and seven trusted aggregator feeds every morning." },
            { q: "Are the notices verified?", a: "Yes. Every entry is checked against the official source PDF before going live. Found an error? Email editor@hireds.in and we fix it within 24 hours." },
            { q: "Can I apply from hireds.in?", a: "We link to the official apply portal. Click the Apply button on a posting page and you are taken straight to the recruiting body's online form." },
            { q: "How do I get free email alerts?", a: "Use the subscribe box on the home page. We send a clean daily or weekly digest. No spam. Stop anytime." },
            { q: "Which sectors does hireds.in cover?", a: "Central govt (UPSC, SSC, RRB), Banks (SBI, IBPS, RBI), Defense (Army, Navy, Air Force, Agniveer), Teaching (KVS, NVS, CTET), every State PSC, and big PSUs like ONGC, GAIL, IOCL, BHEL and NTPC." },
            { q: "Where are admit cards and results published?", a: "Visit the Admit Card and Result tabs from the top menu. Each page lists every exam by date with a direct download link from the official source." },
            { q: "Who runs hireds.in?", a: "hireds.in is operated from Pune, Maharashtra, India by an editorial team led by Dileshwar Vitthale (Chief Editor). See the About page for full details." },
          ].map((it, i) => (
            <details key={i} className="group bg-white border border-slate-200 rounded-lg p-4 hover:border-orange-300 transition-colors" data-testid={`faq-${i}`}>
              <summary className="cursor-pointer min-h-11 inline-flex items-center font-semibold text-slate-900 text-base list-none w-full">
                <span className="flex-1">{it.q}</span>
                <span className="ml-2 text-orange-600 group-open:rotate-45 transition-transform text-xl leading-none" aria-hidden="true">+</span>
              </summary>
              <p className="mt-3 text-sm text-slate-700 leading-relaxed">{it.a}</p>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
}

function Briefcase(){return null}
